// src/config/axios.config.js

import axios from 'axios';
import tenantService from '../services/tenant.service';
import { getApiBaseUrl } from '../utils/api';

// Create axios instance
const axiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable sending cookies for CORS
});

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    // Dynamically set baseURL for each request to handle tenant changes
    config.baseURL = getApiBaseUrl();

    // Add auth token if available
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add tenant identifier header
    const tenantIdentifier = tenantService.getTenantIdentifier();
    if (tenantIdentifier) {
      config.headers['X-Tenant'] = tenantIdentifier;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      console.log('401 Unauthorized - Clearing auth and redirecting to login');

      // Clear all auth-related data
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      localStorage.removeItem('tenant');

      // Check if we're not already on the login page to avoid redirect loop
      if (!window.location.pathname.includes('/login')) {
        // Small delay to allow any error messages to be shown
        setTimeout(() => {
          window.location.href = '/app/login';
        }, 100);
      }
    }

    // Handle expired/inactive subscription (402 from EnsureActiveSubscription).
    // Do NOT log out — the user should stay in to view existing data and renew.
    // Surface a message so the UI can show a "renew" banner; the calling
    // component's own catch still runs (it usually toasts the message).
    if (error.response?.status === 402 && error.response?.data?.code === 'subscription_expired') {
      const msg = error.response?.data?.message || 'Your subscription has expired. Please renew to continue.';
      sessionStorage.setItem('subscriptionExpiredMessage', msg);
      try {
        window.dispatchEvent(new CustomEvent('subscription-expired', { detail: { message: msg } }));
      } catch (e) {
        // CustomEvent unsupported — message is still in sessionStorage for the UI.
      }
    }

    // Handle plan-gated loan type (403 from the loan-type plan gate). The user
    // tried a loan type their plan doesn't include. Broadcast so the UI can show
    // an "upgrade your plan" prompt; the caller's catch still toasts the message.
    if (error.response?.status === 403 && error.response?.data?.code === 'loan_type_not_in_plan') {
      const msg = error.response?.data?.message || "This loan type isn't included in your plan.";
      try {
        window.dispatchEvent(new CustomEvent('loan-type-not-in-plan', { detail: { message: msg } }));
      } catch (e) {
        // CustomEvent unsupported — caller's catch still surfaces the message.
      }
    }

    // Handle storage quota exceeded (413 from the storage gate in
    // TenantStorageService::upload). The company's shared storage pool is full.
    // Broadcast so the UI can show a "storage full / upgrade" prompt with the
    // usage snapshot; the caller's own catch still toasts the message. The user
    // is NOT logged out — they can delete files or upgrade to free up space.
    if (error.response?.status === 413 && error.response?.data?.code === 'storage_limit_reached') {
      const msg = error.response?.data?.message || 'Storage limit reached. Free up space or upgrade your plan to upload more.';
      sessionStorage.setItem('storageLimitMessage', msg);
      try {
        window.dispatchEvent(new CustomEvent('storage-limit-reached', {
          detail: { message: msg, usage: error.response?.data?.usage || null },
        }));
      } catch (e) {
        // CustomEvent unsupported — message is still in sessionStorage for the UI.
      }
    }

    // Handle disabled account (403 with ACCOUNT_DISABLED error code)
    if (error.response?.status === 403 && error.response?.data?.error_code === 'ACCOUNT_DISABLED') {
      console.log('403 Account Disabled - Clearing auth and redirecting to login');

      // Clear all auth-related data
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      localStorage.removeItem('tenant');

      // Store the disabled message to show on login page
      sessionStorage.setItem('accountDisabledMessage', error.response?.data?.message || 'Your account has been disabled. Please contact administrator.');

      // Check if we're not already on the login page to avoid redirect loop
      if (!window.location.pathname.includes('/login')) {
        // Redirect to login with error parameter
        setTimeout(() => {
          window.location.href = '/app/login?error=account_disabled';
        }, 100);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;